import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
const apiUrl = import.meta.env.VITE_FIREBASE_API_URL;
import { useUserStore } from "../../store/userStore";
import { type i18n as I18nType, type TFunction } from "i18next";
import { auth } from "../../firebaseConfig";
import { FirebaseError } from "firebase/app";
import { type CustomAlertProps } from "../../components/base/CustomAlert";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import {
  TRUCK_YARD_INSPECTOR_ROLE_ID,
  TRUCK_YARD_GUARD_ROLE_ID,
  // Legacy alias
  TRUCK_YARD_SUPERVISOR_ROLE_ID,
  isPtarYardRoleId,
  resolvePtarDefaultRoute,
  getPrimaryYardIdForSession,
  getAssignedYardsLabel,
} from "../../hooks/useTruckYardRole";
import { WEB_SERVICE_ROLE_ID } from "../../hooks/useIsWebServiceRole";
import Swal from "sweetalert2";
import api from "../../utils/api";
import axios from "axios";
import {
  validateGuardSupervisorAccess,
  requestLocationPermission,
} from "../../utils/accessValidation";
import { clearAuthStorage, clearRoleStorage } from "../../utils/tokenUtils";
import { trackDiagnostic } from "../../utils/diagnostics";
import { classifyLoginFailure, trackLoginFailure } from "./loginDiagnostics";
import { brand } from "../../config/theme";

export const logoutUser = async () => {
  const { setError, setUser } = useUserStore.getState();
  try {
    const token = localStorage.getItem("token");
    const sessionId = localStorage.getItem("sessionId");

    const fcmToken = localStorage.getItem("tokenCFM");
    const userId = localStorage.getItem("userId");
    if (token && sessionId && fcmToken && userId) {
      try {
        await axios.post(
          `${apiUrl}/notifications/unsuscribe`,
          { tokenCFM: fcmToken, id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-session-id": sessionId,
              "x-user-id": userId,
            },
          },
        );
      } catch (error) {
        console.error("Error unsubscribing FCM token on logout:", error);
      }
    }

    if (sessionId && token) {
      try {
        await api.put(
          `${apiUrl}/session/close`,
          {},
          {
            params: { id: sessionId, sessionId: sessionId },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } catch (error) {
        console.error("Error closing session in backend:", error);
      }
    }

    await signOut(auth);

    clearAuthStorage();

    setUser(null);
    window.location.href = "/";
  } catch (err) {
    setError("Error al cerrar sesión");
    console.error("Logout error:", err);
  }
};

// Helper function to check if input is email
const isEmail = (input: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
  return emailRegex.test(input);
};

// Helper function to get email by username
const getEmailFromUsername = async (
  username: string,
): Promise<string | null> => {
  try {
    const res = await api.get(
      `${apiUrl}/user/email-by-username?username=${encodeURIComponent(username)}`,
    );

    const email = res.data?.data?.email || res.data?.email || null;
    return email;
  } catch (error) {
    return null;
  }
};

export const authenticateUser = async (
  emailOrUsername: string,
  password: string,
  navigate: any,
  showAlert: (alert: CustomAlertProps) => void,
  t: TFunction<"translation", undefined>,
  shouldRedirect: boolean = true,
) => {
  const { setUser, setLoading, setError } = useUserStore.getState();
  setLoading(true);
  setError(null);

  try {
    // Determine if input is email or username
    let email = emailOrUsername;

    if (!isEmail(emailOrUsername)) {
      // Input is username, get the associated email
      const fetchedEmail = await getEmailFromUsername(emailOrUsername);
      if (!fetchedEmail) {
        trackDiagnostic("login_failed", {
          code: "LOGIN_USER_NOT_FOUND",
          login_identifier: emailOrUsername,
          identifier_is_email: false,
        });
        showAlert({
          title: t("errorLogin"),
          text: t("invalidUsernameOrPassword"),
          icon: "error",
          confirmButtonText: t("accept"),
        });
        setError(t("invalidUsernameOrPassword"));
        setLoading(false);
        return;
      }
      email = fetchedEmail;
    }

    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const idToken = await userCredential.user.getIdToken();

    // Primero llamar /login con checkOnly=true para obtener role sin crear sesión
    const checkRes = await api.post(`${apiUrl}/login`, {
      idToken,
      checkOnly: true,
    });
    const userData = checkRes.data.data.data;

    if (!userData) {
      await signOut(auth);
      setError(t("errorLogin"));
      setLoading(false);
      return;
    }

    // Validar acceso para guardias y supervisores (geolocalización y horario)
    const requiresAccessValidation =
      userData.role_id === TRUCK_YARD_GUARD_ROLE_ID ||
      userData.role_id === TRUCK_YARD_SUPERVISOR_ROLE_ID ||
      userData.role_id === TRUCK_YARD_INSPECTOR_ROLE_ID;

    // Toggle de la ficha de personal (Patios > Personal). Apagado, no se pide
    // permiso de ubicación ni se compara contra la geocerca; el horario se
    // sigue validando, así que la validación de acceso corre igual.
    const geofenceEnabled = userData.geofence_validation_enabled !== false;

    if (requiresAccessValidation && geofenceEnabled) {
      // Mostrar explicación y solicitar permisos de ubicación
      const permissionRequest = await Swal.fire({
        title: t("locationPermissionRequired"),
        text: t("locationPermissionDesc"),
        icon: "info",
        showCancelButton: true,
        confirmButtonText: t("accept"),
        cancelButtonText: t("cancel"),
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonColor: brand.primary,
        cancelButtonColor: "#6b7280",
      });

      if (!permissionRequest.isConfirmed) {
        // Usuario canceló, cerrar sesión de Firebase y salir (sin sesión backend)
        await signOut(auth);
        setError(t("locationPermissionDenied"));
        setLoading(false);
        Swal.fire({
          title: t("locationPermissionDenied"),
          text: t("locationPermissionDeniedDesc"),
          icon: "error",
          confirmButtonText: t("accept"),
          confirmButtonColor: brand.primary,
        });
        return;
      }

      // Solicitar permisos de ubicación explícitamente
      const locationPermission = await requestLocationPermission();

      if (!locationPermission) {
        // Usuario negó ubicación, cerrar sesión de Firebase y salir (sin sesión backend)
        await signOut(auth);
        setError(t("locationPermissionDenied"));
        setLoading(false);
        Swal.fire({
          title: t("locationPermissionDenied"),
          text: t("locationPermissionDeniedDesc"),
          icon: "error",
          confirmButtonText: t("accept"),
          confirmButtonColor: brand.primary,
        });
        return;
      }
    }

    if (requiresAccessValidation) {
      // Validar geocercas y horario antes de crear sesión
      Swal.fire({
        title: t("validatingAccess"),
        text: geofenceEnabled ? t("checkingLocation") : t("checkingSchedule"),
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const accessResult = await validateGuardSupervisorAccess(
        {
          yard_id: userData.yard_id,
          yard_ids: userData.yard_ids,
          schedule: userData.schedule,
          geofence_validation_enabled: geofenceEnabled,
        },
        idToken, // Usar el Firebase idToken para la validación
      );

      Swal.close();

      if (!accessResult.allowed) {
        await signOut(auth);
        let title = t("accessDenied");
        let text = "";

        switch (accessResult.reason) {
          case "outside_geofence":
            title = t("outsideGeofence");
            text = t("outsideGeofenceDesc", {
              yardName: accessResult.yardName || "el patio",
            });
            break;
          case "outside_schedule":
            title = t("outsideSchedule");
            text = t("outsideScheduleDesc", {
              from: accessResult.scheduleInfo?.from || "",
              to: accessResult.scheduleInfo?.to || "",
              currentTime: accessResult.scheduleInfo?.currentTime || "",
            });
            break;
          case "no_yard_assigned":
            title = t("noYardAssigned");
            text = t("noYardAssignedDesc");
            break;
          case "geolocation_error":
            title = t("geolocationError");
            text = t("geolocationErrorDesc");
            break;
        }

        setError(title);
        setLoading(false);
        await Swal.fire({
          title,
          text,
          icon: "warning",
          confirmButtonText: t("accept"),
          confirmButtonColor: brand.primary,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        return;
      }
    }

    // Ahora llamar /login normal para crear la sesión
    const res = await api.post(`${apiUrl}/login`, { idToken });

    const data = res.data;

    if (data.data && data.data.token) {
      const previousUserId = localStorage.getItem("userId");
      if (
        data.data.userId &&
        previousUserId &&
        previousUserId !== data.data.userId
      ) {
        clearAuthStorage();
      }

      localStorage.setItem("token", data.data.token);

      if (data.data.sessionId) {
        localStorage.setItem("sessionId", data.data.sessionId);
      }

      if (data.data.companyId) {
        localStorage.setItem("userCompanyId", data.data.companyId);
      }

      if (data.data.userId) {
        localStorage.setItem("userId", data.data.userId);
      }

      const userEmail = userCredential.user.email;
      let userDataFinal = data.user || data.data;
      let roleName = "";

      if (userEmail) {
        try {
          const userRes = await api.get(
            `${apiUrl}/user?email=${encodeURIComponent(userEmail)}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${data.data.token}`,
              },
            },
          );
          const userData = userRes.data;
          if (userData && (userData.user || userData.data)) {
            userDataFinal = userData.user || userData.data;

            // Validar que el role_id no esté desincronizado
            const loginRoleId = userData.data?.data?.role_id || userData.data?.role_id;
            if (loginRoleId && userDataFinal.role_id && loginRoleId !== userDataFinal.role_id) {
              console.warn(
                "[AUTH] Role ID mismatch detected! /login returned:",
                loginRoleId,
                "but /user returned:",
                userDataFinal.role_id,
                "- Using /user endpoint value (fresher)"
              );
            }
          }

          // Obtener información del rol si existe role_id
          if (userDataFinal?.role_id) {
            try {
              const roleRes = await api.get(
                `${apiUrl}/role?id=${encodeURIComponent(userDataFinal.role_id)}`,
                {
                  method: "GET",
                  headers: {
                    Authorization: `Bearer ${data.data.token}`,
                  },
                },
              );
              const roleData = roleRes.data;

              if (roleData?.data?.data?.name) {
                roleName = roleData.data.data.name;
                localStorage.setItem("userRole", roleName);
                localStorage.setItem("userRoleId", userDataFinal.role_id);
                console.debug("[AUTH] Role persisted to localStorage:", { roleName, roleId: userDataFinal.role_id });

                // Los patios de la sesión (issue #497). Para supervisor e
                // inspector se guardaba `yard_ids`, que sus documentos no
                // tenían — tenían `yard_id` —, así que quedaba `[]` y el
                // recorte por rol se apagaba solo.
                //
                // El admin también se persiste, aunque `hasYardRestrictions()`
                // ya no lo recorta (ver useTruckYardRole.ts: 3 admins ACTIVOS
                // de PTAR en producción no tienen ningún patio). Se guarda
                // igual porque es el dato del documento y el enforcement del
                // servidor de #495 puede querer leerlo; lo que NO hace es
                // decidir qué ve.
                //
                // La condición se lee de la tabla de roles de patios y ya no
                // de una lista escrita a mano: el admin de PATIOS (#558) no
                // estaba en esa lista, así que su sesión nunca escribía
                // `userYardIds` — y como `hasYardRestrictions()` SÍ lo recorta,
                // ahora eso lo dejaría con alcance `none` y todas las pantallas
                // vacías. Un rol de patios nuevo que se sume a la tabla queda
                // cubierto acá sin tocar el login.
                if (isPtarYardRoleId(userDataFinal.role_id)) {
                  // `/user` devuelve el documento crudo, así que hasta que corra
                  // `migrateUserYardIds.js` puede venir con el escalar viejo.
                  const rawYardIds = Array.isArray(userDataFinal.yard_ids)
                    ? userDataFinal.yard_ids.filter(Boolean)
                    : [];
                  const yardIds =
                    rawYardIds.length > 0
                      ? rawYardIds
                      : userDataFinal.yard_id
                        ? [userDataFinal.yard_id]
                        : [];
                  // El escalar es SÓLO el respaldo legacy de una sesión de un
                  // patio (getUserAssignedYardIds lo lee cuando el plural falta
                  // o viene vacío). Con `yardIds[0]` el login volvía a nombrar
                  // un patio arbitrario para los multi-patio — justo lo que
                  // `getAssignedYardsLabel` existe para evitar: en producción
                  // los 5 supervisores tienen los 7 patios y los cinco comparten
                  // el mismo `yard_ids[0]` (AMMr7aTFrqOHyMIbiOKf), así que a los
                  // cinco les quedaba fijado el mismo patio equivocado, y en
                  // localStorage, que sobrevive al deploy.
                  const yardId = getPrimaryYardIdForSession(yardIds);
                  localStorage.setItem("userYardIds", JSON.stringify(yardIds));
                  localStorage.setItem("userYardId", yardId);

                  // Toggle de validación por geocerca (ficha de personal). Si
                  // /user no trae el campo, cae al valor que ya resolvió el
                  // checkOnly de /login, que sí lo normaliza.
                  localStorage.setItem(
                    "userGeofenceValidation",
                    String(
                      userDataFinal.geofence_validation_enabled ??
                        geofenceEnabled,
                    ),
                  );

                  // Cómo se nombra el alcance de patios en el encabezado.
                  //
                  // Se busca por TODOS los asignados, no por el escalar: con el
                  // `find` sobre `yardId` el multi-patio quedaba sin nombre
                  // (ahora el escalar es "") o, antes, con el nombre de un patio
                  // arbitrario. `getAssignedYardsLabel` es el mismo criterio que
                  // usan TruckYardLayout e InspectionsLayout — un nombre con uno
                  // solo, "N patios" con varios — y devuelve "" cuando no hay
                  // nada que nombrar, para no pisar el valor previo con vacío.
                  //
                  // La lista completa se pide igual que antes: `getYardsList`
                  // (truck-yard/truckYardController.js) IGNORA `req.query.id` y
                  // responde una lista paginada, así que pedir un id suelto
                  // devolvía un array y `.name` siempre `undefined` — el mismo
                  // defecto que `accessValidation.ts` documenta.
                  if (yardIds.length > 0) {
                    try {
                      const yardRes = await api.get("/truck-yard/yards", {
                        params: { limit: 1000 },
                        headers: {
                          Authorization: `Bearer ${data.data.token}`,
                        },
                      });
                      const list = yardRes.data?.data;
                      const label = Array.isArray(list)
                        ? getAssignedYardsLabel(yardIds, list)
                        : "";
                      if (label) {
                        localStorage.setItem("userYardName", label);
                      }
                    } catch (error) {
                      console.error("Error fetching yard name:", error);
                    }
                  }
                }

                // Si NO es admin_reseller, auto-asignar su compañía como companyId
                if (
                  userDataFinal.role_id !== ADMIN_RESELLER_ROLE_ID &&
                  data.data.companyId
                ) {
                  localStorage.setItem("companyId", data.data.companyId);
                }
              } else {
                console.error(
                  "Respuesta inesperada de /role, se descarta el rol previo:",
                  roleData,
                );
                clearRoleStorage();
              }

              // Obtener nombre de la compañía del usuario
              if (data.data.companyId) {
                try {
                  const companyRes = await api.get(
                    `${apiUrl}/company?id=${encodeURIComponent(data.data.companyId)}`,
                  );
                  // Handle nested response: data.data.data or data.data
                  const companyRaw = companyRes.data?.data;
                  const companyData = companyRaw?.data || companyRaw;
                  const companyName =
                    companyData?.name || companyData?.business_name || "";
                  if (companyName) {
                    localStorage.setItem("userCompanyName", companyName);
                  }
                } catch (e) {
                  console.error("Error fetching company name:", e);
                }
              }
            } catch (e) {
              console.error("Error fetching role:", e);
              clearRoleStorage();
            }
          } else {
            clearRoleStorage();
          }
        } catch (e) {
          console.error("Error fetching user:", e);
          clearRoleStorage();
        }
      }

      // Add company_id to userDataFinal for reactive store access
      if (data.data.companyId && userDataFinal) {
        userDataFinal.company_id = data.data.companyId;
      }

      setUser(userDataFinal);

      // Login nuevo: ir por rol, no a una ruta vieja guardada en una sesión previa.
      localStorage.removeItem("lastRoute");

      // Solo redirigir si shouldRedirect es true
      if (shouldRedirect) {
        // Redirigir según el rol y compañía
        const roleId = userDataFinal?.role_id || "";
        const companyId = data.data.companyId || "";

        if (roleId === WEB_SERVICE_ROLE_ID) {
          navigate("/map");
        } else if (roleName === "web_services") {
          navigate("/entrega-servicio");
        } else {
          // El destino de cada rol de patios sale de la tabla, no de una cadena
          // de `if/else if` paralela a las otras cuatro. Con el rol fuera de la
          // cadena —el caso de #558— se caía al `/map` final y el admin de
          // patios aterrizaba justo en el Mapa de Unidades, que es la app que
          // su rol le quita. `resolvePtarDefaultRoute` devuelve "/map" para
          // todo lo que no sea un rol de patios de PTAR, que es lo que hacía
          // este `else`.
          navigate(resolvePtarDefaultRoute(companyId, roleId));
        }
      }
    } else {
      trackDiagnostic("login_failed", {
        code: "LOGIN_FAILED",
        login_identifier: emailOrUsername,
        identifier_is_email: emailOrUsername.includes("@"),
        backend_message: data.meta?.message || data.message,
      });
      if (data.meta?.error || data.error) {
        showAlert({
          title: t("errorLogin"),
          text: data.meta?.message || data.message || t("unknownError"),
          icon: "error",
          confirmButtonText: t("accept"),
        });
      }
      setError(t("errorLogin"));
    }
  } catch (err) {
    const failureCode = classifyLoginFailure(err);
    trackLoginFailure(err, emailOrUsername, failureCode);

    // Manejar error específico de compañía suspendida
    if (failureCode === "LOGIN_COMPANY_SUSPENDED") {
      await Swal.fire({
        title: "Acceso Denegado",
        text: "No puede acceder a su cuenta debido a que la compañía se encuentra suspendida. Para más atención consulte con soporte.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: brand.primary,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      setError("Compañía suspendida");
      setLoading(false);
      return;
    }

    const errorMessages: Record<string, string> = {
      "auth/invalid-email": t("invalidEmail"),
      "auth/user-not-found": t("userNotFound"),
      "auth/wrong-password": t("wrongPassword"),
      "auth/invalid-credential": t("invalidCredential"),
      "auth/network-request-failed": t("networkRequestFailed"),
      "auth/email-already-in-use": t("emailAlreadyInUse"),
      "auth/weak-password": t("weakPassword"),
      "auth/too-many-requests": t("tooManyRequests"),
    };
    let errorMessage: string;
    if (err instanceof FirebaseError) {
      errorMessage =
        errorMessages[err.code as keyof typeof errorMessages] ??
        t("unknownError");
    } else if (axios.isAxiosError(err) && err.response?.data) {
      // Mensaje del backend: meta.message (formato del errorHandler) o message directo
      const data = err.response.data as {
        meta?: { message?: string };
        message?: string;
      };
      errorMessage = data.meta?.message ?? data.message ?? t("unknownError");
    } else {
      errorMessage = t("unknownError");
    }

    showAlert({
      title: t("errorLogin"),
      text: errorMessage,
      icon: "error",
      confirmButtonText: t("accept"),
    });
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
};

export const toggleSwitch = (
  language: string,
  setIsEnglish: React.Dispatch<React.SetStateAction<boolean>>,
  i18n: I18nType,
) => {
  try {
    i18n.changeLanguage(language);
    setIsEnglish(language === "en");
    localStorage.setItem("@language", language);
  } catch (error) {
    console.error(error);
  }
};

export const resetPassword = async (
  emailOrUsername: string,
  showAlert: (alert: CustomAlertProps) => void,
  t: TFunction<"translation", undefined>,
): Promise<boolean> => {
  try {
    let email = emailOrUsername;

    // Si no es un email, buscar el email asociado al username
    if (!isEmail(emailOrUsername)) {
      const fetchedEmail = await getEmailFromUsername(emailOrUsername);
      if (!fetchedEmail) {
        showAlert({
          title: t("resetPasswordError"),
          text: t("userNotFoundForReset"),
          icon: "error",
          confirmButtonText: t("accept"),
        });
        return false;
      }
      email = fetchedEmail;
    }

    await sendPasswordResetEmail(auth, email);

    showAlert({
      title: t("resetPasswordSuccess"),
      text: t("resetPasswordEmailSent"),
      icon: "success",
      confirmButtonText: t("accept"),
    });
    return true;
  } catch (err) {
    const errorMessages: Record<string, string> = {
      "auth/invalid-email": t("invalidEmail"),
      "auth/user-not-found": t("userNotFound"),
      "auth/too-many-requests": t("tooManyRequests"),
    };

    let errorMessage = t("unknownError");
    if (err instanceof FirebaseError) {
      errorMessage =
        errorMessages[err.code as keyof typeof errorMessages] ||
        t("unknownError");
    }

    showAlert({
      title: t("resetPasswordError"),
      text: errorMessage,
      icon: "error",
      confirmButtonText: t("accept"),
    });
    return false;
  }
};
